import { sql } from "drizzle-orm";
import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
} from "drizzle-orm/sqlite-core";

/**
 * One shared D1, bound into every app's Worker. Rows namespaced by `game`.
 * See ARCHITECTURE.md "Data model".
 */

export const scores = sqliteTable(
  "scores",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    game: text("game").notNull(), // "reflex" | "aim" | "wheel" | ...
    handle: text("handle").notNull(), // chosen display name
    anonId: text("anon_id").notNull(), // localStorage uuid
    value: integer("value").notNull(), // score / ms / streak (meaning is per-game)
    createdAt: integer("created_at", { mode: "timestamp" })
      .notNull()
      .default(sql`(unixepoch())`),
  },
  (t) => [
    // Leaderboard = top N by value for a game (ASC for ms, DESC for score).
    index("scores_game_value_idx").on(t.game, t.value),
    index("scores_anon_idx").on(t.anonId),
  ],
);

export const dailyLocks = sqliteTable(
  "daily_locks",
  {
    anonId: text("anon_id").notNull(), // localStorage uuid
    game: text("game").notNull(),
    lastPlay: integer("last_play", { mode: "timestamp" }).notNull(),
    streak: integer("streak").notNull().default(0),
  },
  (t) => [
    // One lock row per player per game — natural key for the 24h gate.
    primaryKey({ columns: [t.anonId, t.game] }),
  ],
);

/**
 * Mines round ledger. Mines NEEDS per-round server state: with a stateless
 * commitment a client could probe sibling picks for free and walk the board — the
 * ledger makes every reveal part of one irreversible history.
 */
export const minesRounds = sqliteTable(
  "mines_rounds",
  {
    roundId: text("round_id").primaryKey(),
    anonId: text("anon_id").notNull(),
    startedAt: integer("started_at", { mode: "timestamp" }).notNull(),
    /** Comma-separated tile indexes in reveal order ("" = none yet). */
    picks: text("picks").notNull().default(""),
    /** 1 = hit a mine; round dead. */
    dead: integer("dead").notNull().default(0),
    /** 1 = cashed out; round closed. */
    cashed: integer("cashed").notNull().default(0),
  },
  (t) => [index("mines_rounds_anon_idx").on(t.anonId)],
);

/**
 * Shared-event participations (draw picks, rocket boardings). One row per player
 * per round; `result` is written by the lazy resolver after the round closes.
 * The PK doubles as the resolution lock: a synthetic anon_id="_resolved_" row is
 * inserted by whichever request resolves the round first — PK conflict tells
 * every racing resolver to skip the side effects.
 */
export const eventPicks = sqliteTable(
  "event_picks",
  {
    game: text("game").notNull(), // "draw" | "rocket"
    roundId: integer("round_id").notNull(),
    anonId: text("anon_id").notNull(),
    handle: text("handle").notNull(),
    /** draw: chosen slot 0..11. rocket: 0 (boarding marker). */
    choice: integer("choice").notNull(),
    /** draw: 1 hit / 0 miss. rocket: cashout mult ×100, 0 = rode it down. */
    result: integer("result"),
    createdAt: integer("created_at", { mode: "timestamp" })
      .notNull()
      .default(sql`(unixepoch())`),
  },
  (t) => [
    primaryKey({ columns: [t.game, t.roundId, t.anonId] }),
    index("event_picks_round_idx").on(t.game, t.roundId),
  ],
);

export type Score = typeof scores.$inferSelect;
export type NewScore = typeof scores.$inferInsert;
export type DailyLock = typeof dailyLocks.$inferSelect;
export type MinesRound = typeof minesRounds.$inferSelect;
export type EventPick = typeof eventPicks.$inferSelect;
